import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormField, FormTextArea, SmartFormActions } from '../shared/form';
import { SchemaBuilder } from '../schema-builder';
import { useCollaborationConfigs } from '../../hooks/useCollaborationConfigs';
import { useFlag } from '../../hooks/useFeatureFlags';
import { useToast } from '../../contexts/ToastContext';
import {
  EndpointPathInput,
  normalizeEndpointPathValue,
  validateEndpointPath,
} from './EndpointPathInput';
import { describeEndpointSaveError } from './endpointSaveError';
import type { Endpoint } from '../../types';

interface EndpointFormProps {
  initialData?: Partial<Endpoint>;
  onSubmit: (data: any) => Promise<void>;
  onCancel?: () => void;
  mode?: 'create' | 'edit';
}

export function EndpointForm({
  initialData,
  onSubmit,
  onCancel,
  mode = 'create',
}: EndpointFormProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: configsData } = useCollaborationConfigs(1, 100);
  const configs = configsData?.data || [];
  // Queued (async) endpoints are flag-gated (task 336). Flag off → the async
  // option is hidden, a static Sync value is shown, and submits force sync.
  const queuedEnabled = useFlag('innovate.fe.endpoints.queued');

  // Form state
  // Seed in the bare form the prefix adornment expects — stored paths from
  // before the server normalized them may still carry a leading slash.
  const [path, setPath] = useState(normalizeEndpointPathValue(initialData?.path || ''));
  const [method, setMethod] = useState<'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'>(
    initialData?.method || 'POST'
  );
  const [description, setDescription] = useState(initialData?.description || '');
  const [requestFormat, setRequestFormat] = useState(
    initialData?.requestFormat || 'application/json'
  );
  const [responseQueued, setResponseQueued] = useState(
    initialData?.responseQueued ?? false
  );
  const [mcpExposed, setMcpExposed] = useState(
    initialData?.mcpExposed ?? false
  );
  // The chat-pattern opt-in (task 749). Off by default: a service only keeps
  // conversations when its owner says it is a conversation.
  const [threadsEnabled, setThreadsEnabled] = useState(
    initialData?.threadsEnabled ?? false
  );
  const [collaborationConfigId, setCollaborationConfigId] = useState(
    initialData?.collaborationConfigId || ''
  );
  const [inputSchema, setInputSchema] = useState<Record<string, unknown> | null>(
    (initialData?.input as Record<string, unknown>) || null
  );
  const [outputSchema, setOutputSchema] = useState<Record<string, unknown> | null>(
    (initialData?.output as Record<string, unknown>) || null
  );

  // Form validation and submission state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // One validation contract, shared with the inline and bulk editors.
    const pathError = validateEndpointPath(path);
    if (pathError) {
      newErrors.path = pathError;
    }
    if (!method) {
      newErrors.method = 'Method is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const data: any = {
        // Store the bare path — the server strips leading/trailing slashes
        // too, so normalizing here keeps what we display identical to what
        // is stored.
        path: normalizeEndpointPathValue(path),
        method,
        description: description.trim() || undefined,
        requestFormat,
        responseQueued: queuedEnabled ? responseQueued : false,
        mcpExposed,
        threadsEnabled,
        collaborationConfigId: collaborationConfigId || undefined,
      };

      if (inputSchema) {
        data.input = inputSchema;
      }
      if (outputSchema) {
        data.output = outputSchema;
      }

      await onSubmit(data);
    } catch (error) {
      // Never swallow the failure (task 388). A rejected path renders inline on
      // the field the User must change; anything else is a toast, so a save can
      // never look like it simply did nothing.
      const display = describeEndpointSaveError(
        error,
        mode === 'create' ? 'Failed to create AI Service' : 'Failed to save changes'
      );
      if (display.pathError) {
        setErrors(prev => ({ ...prev, path: display.pathError as string }));
      } else {
        toast.error(display.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/endpoints');
    }
  };

  const markDirty = () => {
    if (!isDirty) setIsDirty(true);
  };

  const isValid = path.trim() !== '' && (method as string) !== '';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Method <span className="text-red-500">*</span>
              </label>
              <select
                name="method"
                value={method}
                onChange={(e) => {
                  setMethod(e.target.value as typeof method);
                  markDirty();
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
              {errors.method && (
                <p className="mt-1 text-sm text-red-600">{errors.method}</p>
              )}
            </div>
            <div className="col-span-2">
              <EndpointPathInput
                value={path}
                onChange={(next) => {
                  setPath(next);
                  // Clear a surfaced conflict as soon as the User edits the
                  // path — the error described the old value.
                  if (errors.path) {
                    setErrors((prev) => {
                      const { path: _dropped, ...rest } = prev;
                      return rest;
                    });
                  }
                  markDirty();
                }}
                // Editing an existing endpoint keeps its own owning slug in the
                // prefix; a new one previews under the caller's slug.
                accountSlug={mode === 'edit' ? initialData?.accountSlug : undefined}
                error={errors.path}
              />
            </div>
          </div>

          <FormTextArea
            label="Description"
            name="description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              markDirty();
            }}
            error={errors.description}
            placeholder="Enter endpoint description"
            rows={4}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Collaboration Config
            </label>
            <select
              name="collaborationConfigId"
              value={collaborationConfigId}
              onChange={(e) => {
                setCollaborationConfigId(e.target.value);
                markDirty();
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">None</option>
              {configs.map((config) => (
                <option key={config.id} value={config.id}>
                  {config.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Link this endpoint to a collaboration configuration
            </p>
          </div>

          <FormField
            label="Request Format"
            name="requestFormat"
            type="text"
            value={requestFormat}
            onChange={(e) => {
              setRequestFormat(e.target.value);
              markDirty();
            }}
            placeholder="application/json"
          />

          {queuedEnabled ? (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="responseQueued"
                name="responseQueued"
                checked={responseQueued}
                onChange={(e) => {
                  const queued = e.target.checked;
                  setResponseQueued(queued);
                  if (queued) {
                    setMcpExposed(false);
                  }
                  markDirty();
                }}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <label htmlFor="responseQueued" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Async Response (queued)
              </label>
            </div>
          ) : (
            <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium text-gray-500 dark:text-gray-400 mr-2">Response Mode:</span>
              Sync
            </div>
          )}

          {/* The chat pattern (task 749). Stated in terms of what the caller
              experiences — a conversation that resumes — rather than the
              storage primitive behind it. */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="threadsEnabled"
                name="threadsEnabled"
                checked={threadsEnabled}
                onChange={(e) => {
                  setThreadsEnabled(e.target.checked);
                  markDirty();
                }}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
            </div>
            <div className="ml-2">
              <label
                htmlFor="threadsEnabled"
                className="block text-sm text-gray-700 dark:text-gray-300"
              >
                Conversations (chat pattern)
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Conversations persist and resume for this AI Service: callers continue a durable
                thread with an ordered transcript instead of starting over. Leave off for a
                stateless service that answers each call on its own.
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="mcpExposed"
                name="mcpExposed"
                checked={mcpExposed}
                disabled={queuedEnabled && responseQueued}
                onChange={(e) => {
                  setMcpExposed(e.target.checked);
                  markDirty();
                }}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <div className="ml-2">
              <label
                htmlFor="mcpExposed"
                className={`block text-sm ${responseQueued ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'text-gray-700 dark:text-gray-300'}`}
              >
                Expose as MCP tool
              </label>
              {responseQueued && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Available only for synchronous endpoints (Response Queued off).
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <SchemaBuilder
            label="Input Schema"
            value={inputSchema}
            onChange={(schema) => {
              setInputSchema(schema);
              markDirty();
            }}
            error={errors.input}
          />

          <SchemaBuilder
            label="Output Schema"
            value={outputSchema}
            onChange={(schema) => {
              setOutputSchema(schema);
              markDirty();
            }}
            error={errors.output}
          />
        </div>
      </div>

      <SmartFormActions
        mode={mode}
        isDirty={isDirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onCancel={handleCancel}
        submitLabel={mode === 'create' ? 'Create Endpoint' : 'Save Changes'}
      />
    </form>
  );
}
